import { Application, Assets, Container, Texture, TilingSprite, WRAP_MODES } from 'pixi.js';
import halloweenCharacterFactory from '~/components/Halloween/lib/halloween-character-factory';
import sizeTilingSprite from '~/components/Halloween/lib/size-tiling-sprite';
import type { HalloweenLocation } from '~/types/halloween/location';

export interface HalloweenModalApplication extends Application {
  destroyWithChildren: () => void;
  setLocation: (loc: HalloweenLocation) => void;
  setDirection: (direction: 'left' | 'right') => void;
}

export default async function initPixiApplication(
  holder: HTMLElement,
  location: HalloweenLocation = 'forest'
): Promise<HalloweenModalApplication> {
  let BG_SPEED = 1;
  let FG_SPEED = 2;

  const app = new Application({ resizeTo: holder, backgroundAlpha: 0, antialias: true });
  holder.appendChild(app.view as HTMLCanvasElement);

  Assets.add({ alias: 'caveBackground', src: '/img/expedition/PescheraBack_01.png' });
  Assets.add({ alias: 'caveForeground', src: '/img/expedition/PescheraFront_01.png' });
  Assets.add({ alias: 'forestBackground', src: '/img/expedition/LesBack_01.png' });
  Assets.add({ alias: 'forestForeground', src: '/img/expedition/LesFront_01.png' });

  const loaded = await Assets.load([
    'caveBackground',
    'caveForeground',
    'forestBackground',
    'forestForeground'
  ] as const);

  await halloweenCharacterFactory.createLoadBatch([1, 2, 3, 4, 5, 6]);

  const getTexPair = (loc: HalloweenLocation) => ({
    bg: (loc === 'forest' ? loaded.forestBackground : loaded.caveBackground) as Texture,
    fg: (loc === 'forest' ? loaded.forestForeground : loaded.caveForeground) as Texture
  });

  const { bg: initBgTex, fg: initFgTex } = getTexPair(location);

  initBgTex.baseTexture.wrapMode = WRAP_MODES.REPEAT;
  initFgTex.baseTexture.wrapMode = WRAP_MODES.REPEAT;

  const layer = new Container();
  app.stage.addChild(layer);

  halloweenCharacterFactory.init(app, layer);

  const bg = new TilingSprite(initBgTex, app.screen.width, app.screen.height);
  const fg = new TilingSprite(initFgTex, app.screen.width, app.screen.height);

  bg.zIndex = 1;
  fg.zIndex = 30;

  bg.tileScale.set(0.9, 0.9);
  fg.tileScale.set(0.9, 0.9);

  layer.addChild(bg, fg);

  const onResize = () => {
    const { width: W, height: H } = app.screen;

    bg.width = Math.ceil(W);
    bg.height = Math.ceil(H);
    fg.width = Math.ceil(W);
    fg.height = Math.ceil(H);

    sizeTilingSprite(bg, bg.texture, W, H, 'fit-height');
    sizeTilingSprite(fg, fg.texture, W, H, 'fit-height');
  };
  onResize();
  app.renderer.on('resize', onResize);

  let bgOff = 0,
    fgOff = 0;
  app.ticker.add((delta) => {
    bgOff += BG_SPEED * delta;
    fgOff += FG_SPEED * delta;

    bg.tilePosition.x = -(bgOff | 0);
    fg.tilePosition.x = -(fgOff | 0);
  });

  layer.sortableChildren = true;
  layer.sortChildren();

  (app as any).destroyWithChildren = () => {
    app.ticker.stop();
    app.destroy(true, { children: true, texture: true, baseTexture: true });
  };

  const updateLocation = (next: HalloweenLocation) => {
    const { bg: nextBgTex, fg: nextFgTex } = getTexPair(next);

    nextBgTex.baseTexture.wrapMode = WRAP_MODES.REPEAT;
    nextFgTex.baseTexture.wrapMode = WRAP_MODES.REPEAT;

    bg.texture = nextBgTex;
    fg.texture = nextFgTex;

    const { width: W, height: H } = app.screen;
    sizeTilingSprite(bg, nextBgTex, W, H, 'fit-height');
    sizeTilingSprite(fg, nextFgTex, W, H, 'fit-height');

    bg.texture.update();
    fg.texture.update();

    bgOff = 0;
    fgOff = 0;

    app.render();
  };
  const updateDirection = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      BG_SPEED = -1;
      FG_SPEED = -2;
    } else {
      BG_SPEED = 1;
      FG_SPEED = 2;
    }
  };

  // expose for external usage
  (app as any).setLocation = updateLocation;
  (app as any).setDirection = updateDirection;

  return app as Application & {
    destroyWithChildren: () => void;
    setLocation: (loc: HalloweenLocation) => void;
    setDirection: (direction: 'left' | 'right') => void;
  };
}
